import { useEffect, useState } from "react";

interface BlobUrlState {
  blobUrl: string | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches `url`'s bytes client-side and exposes them as a `blob:` URL.
 *
 * The presigned content URL can't be handed to the DOM directly: S3 signs it
 * with `Content-Disposition: attachment`, which Chrome blocks as an
 * unsolicited automatic download when framed in an `<iframe>`, and the app's
 * CSP `object-src` doesn't allow the S3 origin for an `<embed>`/`<object>`
 * either. A plain `fetch()` is governed by `connect-src` instead (which does
 * allow it), and the resulting `blob:` URL satisfies `object-src` (it
 * explicitly allows `blob:`) and carries no `Content-Disposition` of its own.
 * @param {string | undefined} url - The resolved presigned content URL.
 * @param {boolean} enabled - Whether the fetch should run.
 * @returns {BlobUrlState} The blob URL (once ready) plus loading/error state.
 */
export function useContentBlobUrl(url: string | undefined, enabled: boolean): BlobUrlState {
  const [state, setState] = useState<BlobUrlState>({
    blobUrl: undefined,
    isLoading: enabled && !!url,
    isError: false,
  });

  useEffect(() => {
    if (!enabled || !url) {
      setState({ blobUrl: undefined, isLoading: false, isError: false });
      return;
    }

    setState({ blobUrl: undefined, isLoading: true, isError: false });

    let isMounted = true;
    let objectUrl: string | undefined;
    const abortController = new AbortController();

    const fetchBlob = async () => {
      try {
        const response = await fetch(url, { signal: abortController.signal });
        if (!response.ok) {
          throw new Error(`Failed to fetch document content: ${response.status}`);
        }
        const blob = await response.blob();
        if (!isMounted) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setState({ blobUrl: objectUrl, isLoading: false, isError: false });
      } catch (error) {
        if (isMounted && !(error instanceof Error && error.name === "AbortError")) {
          setState({ blobUrl: undefined, isLoading: false, isError: true });
        }
      }
    };

    fetchBlob();

    return () => {
      isMounted = false;
      abortController.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [url, enabled]);

  return state;
}
